"use client"

import Hamburger from "hamburger-react"
import Link from "next/link"
import { useEffect, useState } from "react"
import { usePathname } from "next/navigation"
import { User } from "@/app/generated/prisma"
import { loginPathFor, signupPathFor } from "@/lib/auth-stack"
import { buttonVariants } from "../../Button"
import SearchBar from "../../Search/SearchBar"
import { UserTile } from "../desktop/userTile"
import { MobileMenu } from "./navmenu"
import { GoPerson } from "react-icons/go"

export function MobileNavBar({
   isAuthenticated,
   isSessionLoading,
   user,
   latestVideoSlug,
}: {
   latestVideoSlug: string
   isAuthenticated: boolean
   isSessionLoading: boolean
   // Passed down rather than re-read from useSession(), so this and the desktop navbar can
   // never render different answers for the same moment.
   user: Partial<User> | undefined
}) {
   const [isOpen, setOpen] = useState<boolean>(false)

   const currentPath = usePathname()

   const toggleOpen = () => {
      setOpen((prev) => !prev)
   }

   useEffect(() => {
      if (isOpen) {
         document.body.style.overflow = "hidden"
      } else {
         document.body.style.overflow = "unset"
      }

      return () => {
         document.body.style.overflow = "unset"
      }
   }, [isOpen])
   return (
      <div
         className="fixed top-0 z-50 h-[98px] w-full  bg-nav-bg px-4 py-6"
         style={{
            boxShadow: "0 0 8px 0 #78788c33",
         }}
      >
         <div className="flex items-center justify-between">
            <Link aria-label="Navigate to home page" href="/">
               {/* eslint-disable-next-line @next/next/no-img-element */}
               <img
                  src="/_logos/logo-restaurant-association-icon-only.svg"
                  alt="Restaurant Association"
                  width={60}
                  height={40}
                  fetchPriority="high"
                  className="h-auto transform-gpu lg:w-full [.dark_&]:hidden"
               />
            </Link>

            <div className="ml-auto flex items-center justify-center gap-1.5">
               <div className="flex h-[36px] w-[36px] items-center justify-center">
                  <SearchBar toggleNavMenu={() => {}} showText={false} className="h-[36px] w-[36px]" />
               </div>

               <div className="flex h-[32px] w-[32px] items-center justify-center">
                  {isSessionLoading ? (
                     <div className="h-[32px] w-[32px] animate-pulse rounded-md bg-gray-200" />
                  ) : isAuthenticated && user ? (
                     <UserTile user={user} isMobile />
                  ) : (
                     <Link
                        aria-label="Login"
                        href={loginPathFor(currentPath)}
                        className="flex h-[32px] w-[32px] items-center justify-center rounded-md bg-custom-primary"
                     >
                        <GoPerson className="h-4 w-4 text-[#FAECEC]" aria-hidden="true" />
                     </Link>
                  )}
               </div>

               {!isSessionLoading && !isAuthenticated && (
                  <div className="flex h-[32px] items-center justify-center">
                     <Link
                        aria-label="Join Free"
                        href={signupPathFor(currentPath)}
                        className={buttonVariants({
                           variant: "outline",
                           className:
                              "h-[32px] whitespace-nowrap rounded-md border-brand-orange px-3 text-sm text-brand-orange hover:bg-brand-orange/10",
                        })}
                     >
                        Join Free
                     </Link>
                  </div>
               )}
            </div>

            <div className="flex items-center justify-center">
               <Hamburger toggled={isOpen} toggle={toggleOpen} size={20} label="mobile menu" />
            </div>
         </div>

         <nav role="navigation" aria-label="Main navigation">
            <MobileMenu
               isOpen={isOpen}
               toggleMenuAction={toggleOpen}
               isAuthenticated={isAuthenticated}
               latestVideoSlug={latestVideoSlug}
            />
         </nav>
      </div>
   )
}
